import React, { Suspense, lazy, useCallback, useState } from "react";
import HomeHero from "@/components/HomeHero";
import RecentSearches from "@/components/home/RecentSearches";
import TrustStrip from "@/components/home/TrustStrip";
import FeaturedStays from "@/components/home/FeaturedStays";
import HomeDestinations from "@/components/recommendations/HomeDestinations";
import Newsletter from "@/components/home/Newsletter";
import { isNativeApp } from "@/lib/nativeApp";

// The apps' Accueil downloads only in the apps: the website never carries it.
const AppHome = lazy(() => import("@/components/app/home/AppHome"));

/**
 * Home is the stays page. The search comes first and nothing covers it; below
 * it, bands that each earn their place and are left out when they have
 * nothing true to show:
 * 1. the visitor's own recent searches (this device only);
 * 2. three facts about booking here;
 * 3. featured stays, with the listings' own photos and honest prices;
 * 4. "Explore the world": photo cards of cities on every continent, each a
 *    real flight search, with a real dated fare and Toukky's own stays where
 *    they exist, then the other cities Toukky lists stays in;
 * 5. the newsletter.
 * The destination photos are the only stock pictures; their cards rise into
 * place on the first scroll (still under reduced motion). No band waits
 * blank to fade in, no invented rating, scarcity or saving.
 */
export default function Home() {
  const [announcement, setAnnouncement] = useState("");
  // Clearing the recent searches replaces their band with "Undo" (it takes
  // the focus, components/home/RecentSearches): the page says it once more
  // for anyone who reached the band another way.
  const cleared = useCallback((message) => { setAnnouncement(message); }, []);
  // The apps have their own Accueil, the approved Android design (owner, 2026-10-24).
  if (isNativeApp()) return <Suspense fallback={null}><AppHome /></Suspense>;

  return (
    <div data-testid="home-page" className="landing-theme pb-4">
      <HomeHero />
      <div className="flex flex-col gap-12 pt-10 sm:gap-14 sm:pt-12 lg:gap-16 lg:pt-14">
        <RecentSearches onCleared={cleared} />
        <TrustStrip />
        <FeaturedStays />
        <HomeDestinations />
        <Newsletter />
      </div>
      <p role="status" className="sr-only" data-testid="home-announcement">{announcement}</p>
    </div>
  );
}
